import type {
  BrushParams,
  BrushPreset,
  BrushTipType,
} from "@excalidraw/element";

export const MAX_CUSTOM_BRUSHES = 50;
const LS_KEY = "chartoon.brushes";

const KNOWN_TIPS: readonly BrushTipType[] = [
  "round",
  "grain",
  "bristle",
  "fiber",
  "spray",
];

// 0..1 params only — see BrushParams docblocks. `spacing`'s real usable
// range is 0.02..1, but we clamp to the same 0..1 as the rest here; nothing
// downstream divides by spacing so a 0 doesn't blow anything up, and this
// keeps the sanitizer a single flat rule instead of one exception per field.
const UNIT_RANGE_KEYS: readonly (keyof BrushParams)[] = [
  "hardness",
  "grainIntensity",
  "strandCurl",
  "fuzz",
  "spacing",
  "scatter",
  "rotationJitter",
  "opacityJitter",
  "sizePressure",
  "opacityPressure",
  "taper",
  "streamline",
];

const clampUnit = (v: unknown): number =>
  Math.min(1, Math.max(0, Number(v) || 0));

/**
 * Cloud/localStorage-synced brush params cross a trust boundary (corrupt or
 * tampered blob, e.g. a hand-edited server prefs JSON) and feed directly
 * into tip-generation loop bounds (strandCount, grainIntensity) — an
 * unclamped `strandCount: 1e9` hangs the tab the moment a preview renders.
 * Clamp every numeric field to its documented range before it's trusted.
 */
const sanitizeParams = (raw: unknown): BrushParams => {
  const p = (raw ?? {}) as Partial<Record<keyof BrushParams, unknown>>;
  const params = {} as Record<keyof BrushParams, unknown>;
  for (const key of UNIT_RANGE_KEYS) {
    params[key] = clampUnit(p[key]);
  }
  params.tip = KNOWN_TIPS.includes(p.tip as BrushTipType) ? p.tip : "round";
  params.strandCount = Math.min(
    200,
    Math.max(0, Math.round(Number(p.strandCount) || 0)),
  );
  params.followPath = Boolean(p.followPath);
  return params as unknown as BrushParams;
};

const sanitizeBrush = (b: {
  id: unknown;
  name?: unknown;
  size?: unknown;
  params: unknown;
}): BrushPreset => ({
  id: String(b.id),
  name: typeof b.name === "string" ? b.name : "",
  size: Math.min(128, Math.max(1, Number(b.size) || 1)),
  params: sanitizeParams(b.params),
  set: "custom",
});

export const loadCustomBrushes = (): BrushPreset[] => {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((b) => b?.id && b?.params).map(sanitizeBrush)
      : [];
  } catch {
    return [];
  }
};

const persist = (brushes: BrushPreset[]): BrushPreset[] => {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(brushes));
    // excalidraw-app listens and syncs to /api/me prefs (Task 10)
    window.dispatchEvent(new CustomEvent("chartoon-brushes-changed"));
  } catch {
    // localStorage full/blocked — keep in-memory only
  }
  return brushes;
};

export const saveCustomBrush = (preset: BrushPreset): BrushPreset[] => {
  const rest = loadCustomBrushes().filter((b) => b.id !== preset.id);
  return persist(
    [...rest, { ...preset, set: "custom" as const }].slice(-MAX_CUSTOM_BRUSHES),
  );
};

export const deleteCustomBrush = (id: string): BrushPreset[] =>
  persist(loadCustomBrushes().filter((b) => b.id !== id));
